import { logError } from '@hotjar/insights-common/lib/logger';

import { HEATMAP_DATA_TYPES } from '../constants-types/HeatmapDataTypes';
import type {
  DeleteScreenhsotV2RequestDto,
  GetClickHeatDataDto,
  GetHeatDataResponseDto,
  GetHeatDataV2RequestDto,
  GetMoveHeatDataDto,
  GetScreenshotDataResponseDto,
  GetScreenshotDataV2RequestDto,
  GetScrollHeatDataDto,
  PinnedPageDto,
  PinnedPagesDto,
  SavablePinnedPageDto,
  TakeScreenshotV2RequestDto,
  TrackHeatmapViewedRequestDto,
  PinnedPageTrackRequestDto,
  GetRecordingInfoRequestDto,
  GetRecordingInfoResponseDto,
  RetakerRequestDto,
  RetakerHtmlRequestDto,
  GetRawHeatResponseDto,
  GetRawHeatRequestDto,
  GetEngagementZonesDataDto,
} from './dto';

export function mapGetScreenshotDataV2RequestModelToDto(
  model: HeatmapAPI.GetScreenshotDataV2Request,
): GetScreenshotDataV2RequestDto {
  return {
    match_operation: model.matchOperation,
    json_query_string: model.jsonQueryString,
  };
}

export function mapGetScreenshotDataResponseDtoToModel(
  dto: GetScreenshotDataResponseDto,
): HeatmapAPI.GetScreenshotDataResponse {
  return {
    screenshotId: dto.screenshot_id,
    image: dto.image,
    imageSize: dto.image_size,
    isCropped: dto.is_cropped,
    originUrl: dto.origin_url,
    status: dto.status,
    recordingId: dto.recording_id,
    recordingStartTime: dto.recording_start_time,
    recordingCreated: dto.recording_created,
    firstSessionCreated: dto.first_session_created,
    lastSessionCreated: dto.last_session_created,
  };
}

export function mapRetakeScreenshotV2RequestModelToDto(
  model: HeatmapAPI.TakeScreenshotV2Request & HeatmapAPI.RetakerExtraParams,
): RetakerRequestDto {
  return {
    action: 'take_tuned_screenshot',
    match_operation: model.matchOperation,
    json_query_string: model.jsonQueryString,
    replace_document_version: 'v2',
    recording_id: model.recordingId,
    start_time: model.startTime,
    previous_start_time: model.previousStartTime,
  };
}

export function mapRetakeHtmlScreenshotV2RequestModelToDto(
  model: HeatmapAPI.TakeScreenshotV2Request & HeatmapAPI.RetakerHtmlParams,
): RetakerHtmlRequestDto {
  return {
    action: 'take_html_screenshot',
    match_operation: model.matchOperation,
    json_query_string: model.jsonQueryString,
    origin_url: model.url,
    raw_html_content: model.html,
    online_screenshot: Boolean(model.online),
  };
}

export function mapTakeScreenshotV2RequestModelToDto(
  model: HeatmapAPI.TakeScreenshotV2Request & Partial<HeatmapAPI.RetakerParams>,
): TakeScreenshotV2RequestDto | RetakerRequestDto | RetakerHtmlRequestDto {
  if (
    'recordingId' in model &&
    typeof model.recordingId !== 'undefined' &&
    typeof model.startTime !== 'undefined' &&
    typeof model.previousStartTime !== 'undefined'
  ) {
    return mapRetakeScreenshotV2RequestModelToDto(
      model as HeatmapAPI.TakeScreenshotV2Request & HeatmapAPI.RetakerExtraParams,
    );
  }

  if ('html' in model && typeof model.html !== 'undefined') {
    return mapRetakeHtmlScreenshotV2RequestModelToDto(
      model as HeatmapAPI.TakeScreenshotV2Request & HeatmapAPI.RetakerHtmlParams,
    );
  }

  return {
    action: 'take_screenshot',
    match_operation: model.matchOperation,
    json_query_string: model.jsonQueryString,
  };
}

export function mapDeleteScreenshotV2RequestModelToDto(
  model: HeatmapAPI.DeleteScreenshotV2Request,
): DeleteScreenhsotV2RequestDto {
  return {
    match_operation: model.matchOperation,
    json_query_string: model.jsonQueryString,
  };
}

export function mapGetHeatDataV2RequestModelToDto<T extends HeatmapDataType>(
  model: HeatmapAPI.GetHeatDataV2Request<T>,
): GetHeatDataV2RequestDto<T> {
  return {
    heatmap_type: model.type,
    screenshot_id: model.screenshotId,
    url: model.url,
    json_query_string: model.jsonQueryString,
    match_operation: model.matchOperation,
  };
}

export function mapGetHeatDataResponseDtoToModel<T extends HeatmapDataType>(
  type: T,
  dto: GetHeatDataResponseDto<T>,
): HeatmapAPI.GetHeatDataResponse<T>;

export function mapGetHeatDataResponseDtoToModel(
  type: string,
  dto: GetClickHeatDataDto | GetMoveHeatDataDto | GetScrollHeatDataDto | GetEngagementZonesDataDto,
):
  | HeatmapAPI.GetClickHeatmapData
  | HeatmapAPI.GetMoveHeatMapData
  | HeatmapAPI.GetScrollHeatmapData
  | HeatmapAPI.GetEngagementZonesData {
  const commonProperties = {
    averageWindowHeight: dto.average_window_height,
    image: dto.image,
    imageSize: dto.image_size,
    samplingApplies: dto.sampling_applies,
    visitCount: dto.visit_count,
    sessionCount: dto.session_count,
  };

  switch (type) {
    case HEATMAP_DATA_TYPES.CLICK: {
      return {
        type,
        ...commonProperties,
        clickCount: (dto as GetClickHeatDataDto).click_count,
        clickRegions: (dto as GetClickHeatDataDto).click_region_count,
        topClickedElements: (dto as GetClickHeatDataDto).top_clicked_elements,
      };
    }
    case HEATMAP_DATA_TYPES.MOVEMENT: {
      return {
        type,
        ...commonProperties,
      };
    }
    case HEATMAP_DATA_TYPES.SCROLL: {
      return {
        type,
        ...commonProperties,
        scrollReach: (dto as GetScrollHeatDataDto).scroll_reach,
      };
    }
    case HEATMAP_DATA_TYPES.ENGAGEMENT: {
      return {
        type,
        ...commonProperties,
        engagementGrid: (dto as GetEngagementZonesDataDto).engagement_grid,
        zoneSize: (dto as GetEngagementZonesDataDto).zone_size,
      };
    }
    default: {
      const error = new Error(`getHeatData(...): Invalid response object type: [${type}].`);
      logError(error);
      throw error;
    }
  }
}

export function mapTrackHeatmapViewedRequestModelToDto(
  model: HeatmapAPI.TrackRequest,
): TrackHeatmapViewedRequestDto {
  return {
    heatmap_type: model.heatmapType,
    device: model.deviceType,
    match_operation: model.matchOperation,
    results_found: model.resultsFound,
    url: encodeURIComponent(model.url),
    date_range_selected: model.dateRangeLabel,
    date_range_applied: model.dateRangeType,
    date_range_start_timestamp: model.dateRangeStartTimestamp,
    date_range_end_timestamp: model.dateRangeEndTimestamp,
    clicks_count: model.clicksCount,
    session_count: model.sessionCount,
    trigger: model.trigger,
    autocomplete_rank: model.autocompleteRank,
    display_type: model.displayType,
    ...(model.failureReason && { failure_reason: model.failureReason }),
  };
}

export function mapPinnedPageDtoToModel(dto: PinnedPageDto): HeatmapAPI.PinnedPage {
  return {
    id: dto.id,
    name: dto.name,
    pattern: dto.pattern,
    matchOperation: dto.match_operation,
    createdBy: dto.created_by,
    jsonQueryString: dto.json_query_string,
  };
}

export function mapPinnedPagesDtoToModel(dto: PinnedPagesDto): HeatmapAPI.PinnedPages {
  return dto.map(mapPinnedPageDtoToModel);
}

export function mapPinnedPageModelToDto(model: HeatmapAPI.PinnedPage): PinnedPageDto {
  return {
    id: model.id,
    name: model.name,
    pattern: model.pattern,
    match_operation: model.matchOperation,
    created_by: model.createdBy,
    json_query_string: model.jsonQueryString,
  };
}

export function mapSavablePinnedPageModelToDto(
  model: HeatmapAPI.SavablePinnedPage,
): SavablePinnedPageDto {
  return {
    name: model.name,
    pattern: model.pattern,
    match_operation: model.matchOperation,
    json_query_string: model.jsonQueryString,
  };
}

export function mapPinnedPageTrackRequestModelToDto(
  model: HeatmapAPI.PinnedPageTrackRequest,
): PinnedPageTrackRequestDto {
  return {
    pin_title: model.name,
    url: model.pattern,
    match_operation: model.matchOperation,
    trigger: model.trigger,
    display_type: model.displayType,
  };
}

export function mapGetRecordingInfoRequestModelToDto(
  model: HeatmapAPI.GetRecordingInfoRequest,
): GetRecordingInfoRequestDto {
  return {
    json_query_string: model.jsonQueryString,
    heatmap_type: model.heatmapType,
    url: model.url,
    device: model.deviceType,
    match_operation: model.matchOperation,
  };
}

export function mapGetRecordingInfoResponseDtoToModel(
  dto: GetRecordingInfoResponseDto,
): HeatmapAPI.GetRecordingInfoResponse {
  return {
    originUrl: dto.origin_url,
    recordingCreatedTime: dto.recording_created,
    recordingId: dto.recording_id,
    recordingUrl: dto.recording_url,
    startTime: dto.start_time,
  };
}

export function mapGetRawHeatDataResponseDtoToModel(
  dto: GetRawHeatResponseDto,
): HeatmapAPI.GetRawHeatResponse {
  return {
    dataPoints: dto.data_points,
    sessionCount: dto.session_count,
    success: dto.success,
  };
}

export function mapGetRawHeatDataResponseModelToDto(
  model: HeatmapAPI.GetRawHeatV2Request,
): GetRawHeatRequestDto {
  return {
    heatmap_type: model.heatmapType,
    match_operation: model.matchOperation,
    device: model.deviceType,
    url: model.url,
    json_query_string: model.jsonQueryString,
  };
}
